import React, {Fragment} from 'react'
import {useParams} from 'react-router-dom'
import {formatTime, pluralise} from 'admin/lib/format'
import useEffect from 'admin/hooks/useEffect'
import useDocument from 'admin/hooks/useDocument'
import useModal from 'admin/hooks/useModal'
import selectableCollection from 'admin/components/hoc/selectableCollection'
import Pagination from 'admin/components/shared/Pagination'
import InputField from 'admin/components/shared/InputField'
import usePaymentsPending from 'bundles/admin/hooks/usePaymentsPending'
import Page from './shared/Page'
import ModalLink from './shared/ModalLink'
import DateField from './shared/DateField'
import Sorting from './shared/Sorting'
import {sumBy} from 'lodash'
import useStateObject from '../hooks/useStateObject'
import usePayments from '../hooks/usePayments'
import moment from 'moment-timezone'
import Table from './PaymentsPending/Table'
import Dropdown from './shared/Dropdown'
import useOrganisations from '../hooks/useOrganisations'

const sortOptions = [
  {
    sortBy: 'payments.date',
    sortDirection: 'desc',
    sortLabel: 'Date received',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {sortBy: 'payments.statement_details', sortDirection: 'asc', sortLabel: 'Details', ascLabel: 'A-Z', descLabel: 'Z-A'},
  {
    sortBy: 'payments.statement_particulars',
    sortDirection: 'asc',
    sortLabel: 'Particulars',
    ascLabel: 'A-Z',
    descLabel: 'Z-A'
  },
  {sortBy: 'payments.statement_code', sortDirection: 'asc', sortLabel: 'Code', ascLabel: 'A-Z', descLabel: 'Z-A'},
  {
    sortBy: 'payments.statement_reference',
    sortDirection: 'asc',
    sortLabel: 'Reference',
    ascLabel: 'A-Z',
    descLabel: 'Z-A'
  },
  {sortBy: 'amount', sortDirection: 'asc', sortLabel: 'Amount'}
]

export default () => {
  const {pushModal} = useModal()
  const {organisationID} = useParams()
  const {imports, totalCount, filters, setFilters} = usePaymentsPending()
  const {update: updatePayments} = usePayments('pending', {skip: true})
  const {organisations} = useOrganisations()
  const [selectedGroups, setSelectedGroups, resetSelectedGroups] = useStateObject({})
  const selected = Object.values(selectedGroups).flat()

  const title = 'Manage pending payments'

  useDocument({title})

  useEffect(() => resetSelectedGroups(), [imports])

  const handleReconcile = e => {
    e.preventDefault()
    updatePayments({paymentIds: selected, state: 'reconciled'}, () => resetSelectedGroups())
  }

  const handleIgnore = e => {
    e.preventDefault()
    updatePayments({paymentIds: selected, ignoredAt: moment()}, () => resetSelectedGroups())
  }

  const handleMove = (e, targetOrganisationId) => {
    e.preventDefault()
    updatePayments({paymentIds: selected, organisationId: targetOrganisationId}, () => {
      resetSelectedGroups()
    })
  }

  const paymentsCount = sumBy(imports, i => i.payments.length)

  return (
    <Fragment>
      <Page
        title={title}
        subtitle={
          paymentsCount
            ? imports.length > 1
              ? `Showing ${pluralise('payment', paymentsCount, true)} grouped by import`
              : `Showing ${pluralise('pending payment', paymentsCount, true)}`
            : 'No pending payments found'
        }
        buttons={
          <Fragment>
            <ModalLink modal='new-bank-import' className='button is-light'>
              New import
            </ModalLink>
          </Fragment>
        }>
        <form className='box is-radiusless is-shadowless has-background-filters'>
          <div className='field is-grouped'>
            <div className='control is-expanded'>
              <InputField
                type='search'
                name='q'
                label='Search'
                autoComplete='off'
                value={filters.q}
                debounce={true}
                onChange={changes => setFilters({...changes, page: 1})}
              />
            </div>
            <div className='control'>
              <DateField name='fromDate' value={filters.fromDate} onChange={setFilters} />
            </div>
            <div className='control'>
              <DateField name='toDate' value={filters.toDate} onChange={setFilters} />
            </div>
          </div>
        </form>

        {imports.length != 0 ? (
          <Fragment>
            {selected.length ? (
              <div className='level'>
                <div className='level-left has-background-info box mb-0 is-shadowless p-3 pl-4'>
                  <div className='level-item has-text-white'>
                    {selected.length === imports.length ? 'All ' : ''}
                    {pluralise('payments', selected.length, true)} on this page {pluralise('is', selected.length)}{' '}
                    selected
                  </div>
                  <button
                    className='level-item button is-small is-primary has-text-weight-semibold'
                    onClick={handleReconcile}>
                    Reconcile
                  </button>
                  <button className='level-item button is-outlined is-small' onClick={handleIgnore}>
                    Ignore
                  </button>
                  {organisations.length > 1 ? (
                    <Dropdown id='move-menu' className='level-item'>
                      <button className='button is-outlined is-small'>
                        <span>Move to</span>
                        <span className='icon is-small'>
                          <i className='fa fa-caret-down' aria-hidden='true'></i>
                        </span>
                      </button>
                      {organisations
                        .filter(org => org.id !== organisationID)
                        .map((org, i) => (
                          <a
                            key={i}
                            style={org.internalColor ? {borderLeft: `6px solid #${org.internalColor}`} : {}}
                            onClick={e => handleMove(e, org.id)}
                            className='dropdown-item'>
                            {org.internalName}
                          </a>
                        ))}
                    </Dropdown>
                  ) : null}

                  {selected.length === 1 ? (
                    <ModalLink
                      className='level-item button is-outlined is-small'
                      modal='new-payment-rule'
                      params={{paymentId: selected[0]}}>
                      New payment rule
                    </ModalLink>
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className='level'>
              <div className='level-left'>
                <Sorting
                  className='level-item'
                  buttonText={imports.length > 1 ? 'Sort each group by' : null}
                  sortOptions={sortOptions}
                  sortBy={filters.sortBy}
                  sortDirection={filters.sortDirection}
                  onChange={setFilters}
                />
              </div>
              <div className='level-right'>
                <Pagination
                  page={filters.page}
                  limit={filters.limit}
                  total={totalCount}
                  onChange={setFilters}
                  className='level-item'
                />
              </div>
            </div>

            {imports.map((bankImport, key) => {
              const SelectableTable = selectableCollection(Table, {
                onSelect: identifiers => setSelectedGroups({[bankImport.id]: identifiers}),
                selectedIdentifiers: selectedGroups[bankImport.id] || []
              })
              return (
                <SelectableTable data={bankImport.payments} organisationId={organisationID} key={key}>
                  <article
                    className={`message mb-0${bankImport.organisation.internalColor ? '' : ' is-info'} is-small is-clickable`}
                    onClick={e => {
                      e.preventDefault()
                      pushModal('bank-import', {bankImportId: bankImport.id})
                    }}>
                    <div
                      style={
                        bankImport.organisation.internalColor
                          ? {backgroundColor: '#' + bankImport.organisation.internalColor}
                          : {}
                      }
                      className='message-header'>
                      <span>
                        Imported at {formatTime(bankImport.createdAt)}
                        {organisationID !== bankImport.organisation.id ? (
                          <span className='tag is-small is-light is-rounded ml-4'>
                            {bankImport.organisation.internalName}
                          </span>
                        ) : null}
                      </span>
                      <span>{bankImport.name}</span>
                    </div>
                  </article>
                </SelectableTable>
              )
            })}

            {imports.length >= 10 && (
              <div className='level'>
                <div className='level-left'></div>
                <div className='level-right'>
                  <Pagination
                    page={filters.page}
                    limit={filters.limit}
                    total={totalCount}
                    onChange={setFilters}
                    className='level-item'
                  />
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
      </Page>
    </Fragment>
  )
}
